// healthSync.js
// Reads workouts and daily activity from Apple HealthKit via @capgo/capacitor-health
// and upserts them into Supabase. Only runs on iOS native (Capacitor). No-ops elsewhere.

import supabase from '../supabase';
import { Health } from '@capgo/capacitor-health';
import { Capacitor } from '@capacitor/core';
import { reportError } from '../sentry';
import { queueUpsert, flushWriteQueue, installWriteRetry } from './writeRetry';

// ─── READ SCOPES ────────────────────────────────────────────────────────────
// One const for every requestAuthorization call in this file. HealthKit
// permissions are per-type, so a type missing from ONE array means that code
// path silently never prompts for it — the arrays must not drift. Adding a
// type here makes iOS show an incremental permission sheet (listing only the
// new type) to already-granted users on their next sync.
const HEALTH_READ_TYPES = ['workouts', 'steps', 'distance', 'flightsClimbed', 'sleep'];

// ─── IN-FLIGHT GUARDS ───────────────────────────────────────────────────────
// Prevents duplicate runs when both getSession and onAuthStateChange fire.
let _workoutsInflight = null; // { userId, promise }
let _activityInflight = null; // { userId, promise }
let _sleepInflight    = null; // { userId, promise }

export async function isHealthAvailable() {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await Health.isAvailable();
    return !!result?.available;
  } catch (e) {
    return false;
  }
}

export async function requestHealthAccess() {
  if (!Capacitor.isNativePlatform()) return false;
  return authorizeHealth();
}

// ─── AUTHORIZATION (SCORIFY-APP-P) ──────────────────────────────────────────
// Every requestAuthorization in this file goes through authorizeHealth().
// On iOS 27.0.1 (App Review's device) the system permission sheet itself
// failed to open — "FrontBoard failed to launch com.apple.HealthPrivacyService"
// — and the user saw nothing. So: one retry after a short pause, and if it
// still fails, a 'failed' status the UI renders as a calm "try again / turn
// it on in Settings" notice (HealthAuthNotice in components/Health.jsx).
//
// Status is in-memory, per launch: 'unknown' (not attempted / not native),
// 'authorized' (the call returned), 'failed' (threw twice). 'authorized'
// only means the sheet completed — HealthKit never reveals read denial, so
// the data-presence guards in AppleHealthSection still own "receiving data".
const AUTH_RETRY_DELAY_MS = 1500;
const SHEET_FAILED_RE = /FrontBoard|HealthPrivacyService/i;
let _authStatus = 'unknown';
let _authInflight = null; // three syncs start together — share one attempt

export function getHealthAuthStatus() { return _authStatus; }

function setAuthStatus(status) {
  _authStatus = status;
  try { window.dispatchEvent(new CustomEvent('scorify-health-auth', { detail: status })); } catch {}
}

export function authorizeHealth() {
  if (_authInflight) return _authInflight;
  _authInflight = _doAuthorizeHealth().finally(() => { _authInflight = null; });
  return _authInflight;
}

async function _doAuthorizeHealth() {
  const attempt = () => Health.requestAuthorization({ read: HEALTH_READ_TYPES, write: [] });
  try {
    await attempt();
    setAuthStatus('authorized');
    return true;
  } catch (firstErr) {
    console.warn('[healthSync] requestAuthorization failed, retrying once:', firstErr);
    await new Promise((r) => setTimeout(r, AUTH_RETRY_DELAY_MS));
    try {
      await attempt();
      setAuthStatus('authorized');
      return true;
    } catch (err) {
      console.error('[healthSync] requestAuthorization failed after retry:', err);
      const sheetFailed = SHEET_FAILED_RE.test(String(err?.message || err || ''));
      reportError(err, {
        op: 'health.requestAuthorization',
        level: 'warning',
        retried: 'yes',
        fingerprint: [sheetFailed ? 'healthkit-auth-sheet-failed' : 'healthkit-auth-failed'],
      });
      setAuthStatus('failed');
      return false;
    }
  }
}

// "Try again" from the UI: re-request, and on success run the syncs that
// bailed out, so the card fills without a relaunch.
export async function retryHealthAuthorization(userId) {
  if (!Capacitor.isNativePlatform()) return false;
  const ok = await authorizeHealth();
  if (ok && userId) {
    syncHealthWorkouts(userId);
    syncDailyActivity(userId);
    syncSleep(userId);
  }
  return ok;
}

function localDateStr(isoStr) {
  const d = new Date(isoStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── WORKOUT SYNC ─────────────────────────────────────────────────────────────
export function syncHealthWorkouts(userId) {
  if (_workoutsInflight && _workoutsInflight.userId === userId) return _workoutsInflight.promise;
  const promise = _doSyncHealthWorkouts(userId).finally(() => { _workoutsInflight = null; });
  _workoutsInflight = { userId, promise };
  return promise;
}

async function _doSyncHealthWorkouts(userId) {
  console.log('[healthSync] entry — userId:', userId);

  try {
    if (!userId) {
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      console.log('[healthSync] not native platform:', Capacitor.getPlatform());
      return;
    }

    // ── isAvailable ──────────────────────────────────────────────────────────
    let availResult;
    try {
      availResult = await Health.isAvailable();
    } catch (availErr) {
      return;
    }
    console.log('[healthSync] isAvailable raw:', availResult, 'typeof .available:', typeof availResult?.available);
    if (!availResult?.available) {
      return;
    }

    // ── requestAuthorization ─────────────────────────────────────────────────
    // Retry + Sentry + UI status all live in authorizeHealth().
    if (!(await authorizeHealth())) return;

    // ── queryWorkouts ────────────────────────────────────────────────────────
    const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const now   = new Date().toISOString();
    console.log('[healthSync] querying workouts startDate:', since, 'endDate:', now);

    let allWorkouts = [];
    let anchor = undefined;
    let page = 0;
    do {
      page++;
      const opts = { startDate: since, endDate: now, limit: 100 };
      if (anchor !== undefined) opts.anchor = anchor;
      let pageResult;
      try {
        pageResult = await Health.queryWorkouts(opts);
      } catch (qErr) {
        console.error('[healthSync] queryWorkouts error page', page, qErr);
        reportError(qErr, { op: "healthSync.queryWorkouts", page });
        break;
      }
      const batch = pageResult.workouts || [];
      console.log(`[healthSync] page ${page}: raw count=${batch.length}, anchor=${pageResult.anchor ?? 'none'}`);
      allWorkouts = allWorkouts.concat(batch);
      anchor = pageResult.anchor;
    } while (anchor !== undefined && anchor !== null);

    console.log('[healthSync] total workouts fetched:', allWorkouts.length);

    if (allWorkouts.length > 0) {
      const rows = allWorkouts.map((w) => ({
        user_id:      userId,
        source:       'healthkit',
        external_id:  w.platformId || null,
        type:         normalizeType(w.workoutType || ''),
        name:         w.workoutType || 'Workout',
        distance_m:   w.totalDistance     != null ? Math.round(w.totalDistance)     : null,
        calories:     w.totalEnergyBurned != null ? Math.round(w.totalEnergyBurned) : null,
        duration_min: w.duration          != null ? Math.round(w.duration / 60)     : null,
        start_at:     w.startDate || null,
        end_at:       w.endDate   || null,
        date:         w.startDate ? localDateStr(w.startDate) : null,
      }));
      console.log('[healthSync] mapped row count:', rows.length, '— first row:', JSON.stringify(rows[0]));

      for (let i = 0; i < rows.length; i += 50) {
        const chunk = rows.slice(i, i + 50).filter((r) => r.external_id);
        if (chunk.length === 0) continue;
        // Same queue-on-network-failure as daily_activity (last write wins per external id).
        installWriteRetry(userId);
        const r = await queueUpsert({ userId, table: 'workouts', conflict: 'user_id,source,external_id', keyOf: (row) => `${row.source}|${row.external_id}`, rows: chunk });
        if (r.error) {
          console.error('[healthSync] upsert error:', r.error);
          // Row count only — never the workout rows themselves.
          reportError(new Error(`workouts upsert: ${r.error.message}`), {
            op: "healthSync.upsert", code: r.error.code, rows: chunk.length,
          });
        }
      }
    }

    // ── Write health_last_sync_at — always, even on zero-result syncs ────────
    const { error: profileErr } = await supabase
      .from('profiles')
      .update({ health_last_sync_at: new Date().toISOString() })
      .eq('id', userId);
    if (profileErr) {
      console.error('[healthSync] profiles update error:', profileErr);
      reportError(new Error(`health_last_sync_at: ${profileErr.message}`), {
        op: "healthSync.profileUpdate", code: profileErr.code,
      });
    } else {
      console.log('[healthSync] health_last_sync_at updated');
    }

    console.log('[healthSync] syncHealthWorkouts complete');
    // Tell the dashboard fresh rows exist. These syncs (fired from App.js on
    // session start) finish AFTER Scorify.jsx's loadData has already read the
    // fitness tables — without this signal the dashboard held stale (often
    // empty) arrays for the whole session, which getGoalAutoValue's
    // availability guard rendered as a goal stuck on "syncing".
    try { window.dispatchEvent(new CustomEvent('scorify-health-synced')); } catch {}
  } catch (err) {
    console.error('[healthSync] unhandled error:', err);
    reportError(err, { op: "healthSync.unhandled" });
  }
}

// ─── DAILY ACTIVITY SYNC ──────────────────────────────────────────────────────
// Syncs daily steps + distance into `daily_activity`.
// flightsClimbed is not queried — queryAggregated does not support it.
// Window: 30 days first run, 7 days subsequent.
export function syncDailyActivity(userId) {
  flushWriteQueue(userId);
  if (_activityInflight && _activityInflight.userId === userId) return _activityInflight.promise;
  const promise = _doSyncDailyActivity(userId).finally(() => { _activityInflight = null; });
  _activityInflight = { userId, promise };
  return promise;
}

async function _doSyncDailyActivity(userId) {
  console.log('[dailyActivity] entry — userId:', userId);

  try {
    if (!userId) {
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      console.log('[dailyActivity] not native platform:', Capacitor.getPlatform());
      return;
    }

    // ── isAvailable ──────────────────────────────────────────────────────────
    let availResult;
    try {
      availResult = await Health.isAvailable();
    } catch (availErr) {
      return;
    }
    console.log('[dailyActivity] isAvailable raw:', availResult, 'typeof .available:', typeof availResult?.available);
    if (!availResult?.available) {
      return;
    }

    // ── requestAuthorization ─────────────────────────────────────────────────
    if (!(await authorizeHealth())) return;

    // ── Determine query window ───────────────────────────────────────────────
    const { data: profile } = await supabase
      .from('profiles')
      .select('health_last_sync_at')
      .eq('id', userId)
      .single();
    const isFirstRun = !profile?.health_last_sync_at;
    // First connect backfills ~90 days so pattern detection can clear its
    // min-data gates on day one (steps/sleep/workouts); later runs only need the
    // recent tail. Workouts already query 90 days above.
    const daysBack   = isFirstRun ? 90 : 7;

    const endDate   = new Date().toISOString();
    const startDate = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
    console.log('[dailyActivity] isFirstRun:', isFirstRun, 'window:', daysBack, 'days, startDate:', startDate, 'endDate:', endDate);

    // ── queryAggregated for steps and distance ───────────────────────────────
    const safeQuery = async (dataType) => {
      try {
        return await Health.queryAggregated({ dataType, startDate, endDate, bucket: 'day', aggregation: 'sum' });
      } catch (e) {
        console.error(`[dailyActivity] queryAggregated ${dataType} error:`, e);
        reportError(e, { op: "dailyActivity.queryAggregated", dataType });
        return { samples: [] };
      }
    };
    const [stepsResult, distResult] = await Promise.all([
      safeQuery('steps'),
      safeQuery('distance'),
    ]);

    console.log('[dailyActivity] steps samples:', stepsResult.samples?.length,
      'dist samples:', distResult.samples?.length);

    // ── Merge by local date ──────────────────────────────────────────────────
    const byDate = {};
    for (const s of (stepsResult.samples || [])) {
      const dateStr = localDateStr(s.startDate);
      byDate[dateStr] = byDate[dateStr] || {};
      byDate[dateStr].steps = Math.round(s.value ?? s.values?.sum ?? 0);
    }
    for (const s of (distResult.samples || [])) {
      const dateStr = localDateStr(s.startDate);
      byDate[dateStr] = byDate[dateStr] || {};
      byDate[dateStr].distance_m = Math.round(s.value ?? s.values?.sum ?? 0);
    }

    // Skip days where steps + distance both zero/null
    const rows = Object.entries(byDate)
      .filter(([, v]) => (v.steps || 0) + (v.distance_m || 0) > 0)
      .map(([date, v]) => ({
        user_id:    userId,
        date,
        steps:      v.steps      || null,
        distance_m: v.distance_m || null,
        source:     'healthkit',
      }));

    console.log('[dailyActivity] rows to upsert:', rows.length,
      rows.length > 0 ? '— first row: ' + JSON.stringify(rows[0]) : '');

    if (rows.length > 0) {
      // A network failure ("Load failed", SCORIFY-APP-1M) queues the rows and
      // retries on reconnect / next resume, last write wins per date; only a
      // server rejection reports now (writeRetry reports when retries run out).
      installWriteRetry(userId);
      const r = await queueUpsert({ userId, table: 'daily_activity', conflict: 'user_id,date', keyOf: (row) => row.date, rows });
      if (r.error) {
        console.error('[dailyActivity] upsert error:', r.error);
        reportError(new Error(`daily_activity upsert: ${r.error.message}`), {
          op: "dailyActivity.upsert", code: r.error.code, rows: rows.length,
        });
      } else {
        console.log(r.queued ? '[dailyActivity] upsert queued for retry' : '[dailyActivity] upsert complete');
      }
    }

    console.log('[dailyActivity] syncDailyActivity complete');
    // Same signal as syncHealthWorkouts — see the comment there.
    try { window.dispatchEvent(new CustomEvent('scorify-health-synced')); } catch {}
  } catch (err) {
    console.error('[dailyActivity] unhandled error:', err);
    reportError(err, { op: "dailyActivity.unhandled" });
  }
}

// ─── SLEEP SYNC ───────────────────────────────────────────────────────────────
// Reads HealthKit sleepAnalysis samples via readSamples (queryAggregated
// rejects sleep — it is categorical) and upserts one row per NIGHT into
// `sleep_nights`, keyed on the WAKE date.
//
// THE inBed HONESTY RULE: an iPhone-only Sleep Schedule writes `inBed`
// samples that mirror the configured schedule, NOT measured sleep. A night is
// MEASURED only when it carries asleep-family samples (asleep/rem/deep/light,
// written by a Watch worn overnight or a sleep app). We store both, but
// asleep_min stays NULL on inBed-only nights — every downstream reader
// (Health card, cron signals, Kai context) keys "measured" on that null.
//
// ONE WINNING SOURCE PER NIGHT: a Watch and a third-party sleep app both
// write overlapping samples for the same night, and HealthKit returns all of
// them (the Health app dedups in its UI; raw queries do not). Summing across
// sources reports 14-hour nights. Samples are clustered into sessions per
// source, and the night keeps one source's session: stage data preferred,
// then longest measured sleep.
export function syncSleep(userId) {
  if (_sleepInflight && _sleepInflight.userId === userId) return _sleepInflight.promise;
  const promise = _doSyncSleep(userId).finally(() => { _sleepInflight = null; });
  _sleepInflight = { userId, promise };
  return promise;
}

const ASLEEP_STATES = new Set(['asleep', 'rem', 'deep', 'light']);
const STAGE_STATES  = new Set(['rem', 'deep', 'light']);

async function _doSyncSleep(userId) {
  console.log('[sleepSync] entry — userId:', userId);
  try {
    if (!userId) return;
    if (!Capacitor.isNativePlatform()) {
      console.log('[sleepSync] not native platform:', Capacitor.getPlatform());
      return;
    }

    let availResult;
    try {
      availResult = await Health.isAvailable();
    } catch (availErr) {
      return;
    }
    if (!availResult?.available) return;

    if (!(await authorizeHealth())) return;

    // First-run detection keys on sleep_nights itself, NOT health_last_sync_at:
    // that timestamp has been stamping since the workouts sync shipped, so an
    // existing user's FIRST sleep sync would look like a subsequent run and
    // backfill only 7 days instead of 30.
    const { data: existing } = await supabase
      .from('sleep_nights')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    const isFirstRun = !existing || existing.length === 0;
    const daysBack = isFirstRun ? 90 : 7; // first connect backfills ~90 days for pattern detection

    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
    console.log('[sleepSync] isFirstRun:', isFirstRun, 'window:', daysBack, 'days');

    // readSamples has no pagination anchor (unlike queryWorkouts), so the
    // limit must cover the window: a Watch night is ~15-40 stage samples, so
    // 30 nights of multi-source data fits comfortably under 5000.
    let result;
    try {
      result = await Health.readSamples({ dataType: 'sleep', startDate, endDate, limit: 5000, ascending: true });
    } catch (qErr) {
      console.error('[sleepSync] readSamples error:', qErr);
      reportError(qErr, { op: "sleepSync.readSamples" });
      return;
    }
    const samples = (result?.samples || []).filter(s => s.startDate && s.endDate);
    console.log('[sleepSync] samples fetched:', samples.length);
    // Empty is expected and silent: HealthKit returns empty for denial AND for
    // genuinely-no-data, indistinguishably. No rows means unknown, never zero.
    if (samples.length === 0) return;

    // ── Cluster into sessions per source ────────────────────────────────────
    const bySource = new Map();
    for (const s of samples) {
      const src = s.sourceId || s.sourceName || 'unknown';
      if (!bySource.has(src)) bySource.set(src, []);
      bySource.get(src).push(s);
    }

    const GAP_MS = 2 * 60 * 60 * 1000; // samples <2h apart are one session
    const sessions = [];
    for (const [, srcSamples] of bySource) {
      srcSamples.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      let cur = null;
      for (const s of srcSamples) {
        const startMs = Date.parse(s.startDate);
        const endMs = Date.parse(s.endDate);
        if (!cur || startMs - cur.endMs > GAP_MS) {
          if (cur) sessions.push(cur);
          cur = { startMs, endMs, asleepMin: 0, inBedMin: 0, hasStage: false, sourceName: s.sourceName || null };
        }
        cur.endMs = Math.max(cur.endMs, endMs);
        const durMin = Math.max(0, Math.round((endMs - startMs) / 60000));
        if (ASLEEP_STATES.has(s.sleepState)) cur.asleepMin += durMin;
        else if (s.sleepState === 'inBed') cur.inBedMin += durMin;
        if (s.hasStageData || STAGE_STATES.has(s.sleepState)) cur.hasStage = true;
      }
      if (cur) sessions.push(cur);
    }

    // ── One winning session per wake date ───────────────────────────────────
    // Attribute a session to the LOCAL date it ended (the wake date). Naps
    // lose automatically: the longest/measured session wins the date.
    const byDate = new Map();
    for (const sess of sessions) {
      if (sess.asleepMin === 0 && sess.inBedMin === 0) continue;
      const dateStr = localDateStr(new Date(sess.endMs).toISOString());
      const prev = byDate.get(dateStr);
      if (!prev) { byDate.set(dateStr, sess); continue; }
      // Prefer measured over inBed-only; among measured, prefer stage data,
      // then longer asleep time; among inBed-only, longer in-bed time.
      const prevMeasured = prev.asleepMin > 0, curMeasured = sess.asleepMin > 0;
      const wins =
        (curMeasured && !prevMeasured) ||
        (curMeasured === prevMeasured && (
          curMeasured
            ? (sess.hasStage !== prev.hasStage ? sess.hasStage : sess.asleepMin > prev.asleepMin)
            : sess.inBedMin > prev.inBedMin
        ));
      if (wins) byDate.set(dateStr, sess);
    }

    const rows = Array.from(byDate.entries()).map(([date, sess]) => ({
      user_id: userId,
      date,
      asleep_min: sess.asleepMin > 0 ? sess.asleepMin : null, // null = not measured
      in_bed_min: sess.inBedMin > 0 ? sess.inBedMin : null,
      sleep_start_at: new Date(sess.startMs).toISOString(),
      sleep_end_at: new Date(sess.endMs).toISOString(),
      has_stage_data: sess.hasStage,
      source: 'healthkit',
      source_name: sess.sourceName,
    }));
    console.log('[sleepSync] nights to upsert:', rows.length);

    if (rows.length > 0) {
      // Same queue-on-network-failure as daily_activity (last write wins per date).
      installWriteRetry(userId);
      const r = await queueUpsert({ userId, table: 'sleep_nights', conflict: 'user_id,date', keyOf: (row) => row.date, rows });
      if (r.error) {
        console.error('[sleepSync] upsert error:', r.error);
        // Row count only — never the sleep rows themselves.
        reportError(new Error(`sleep_nights upsert: ${r.error.message}`), {
          op: "sleepSync.upsert", code: r.error.code, rows: rows.length,
        });
        return;
      }
    }

    console.log('[sleepSync] syncSleep complete');
    // Same signal as syncHealthWorkouts — see the comment there. The Health
    // card reloads its sleep query on this event too.
    try { window.dispatchEvent(new CustomEvent('scorify-health-synced')); } catch {}
  } catch (err) {
    console.error('[sleepSync] unhandled error:', err);
    reportError(err, { op: "sleepSync.unhandled" });
  }
}

function normalizeType(raw) {
  const s = (raw || '').toLowerCase();
  if (s.includes('run'))                                                    return 'Run';
  if (s.includes('walk'))                                                   return 'Walk';
  if (s.includes('hike'))                                                   return 'Hike';
  if (s.includes('cycl') || s.includes('ride') || s.includes('bike'))      return 'Ride';
  if (s.includes('swim'))                                                   return 'Swim';
  if (s.includes('weight') || s.includes('strength') || s.includes('functional')) return 'WeightTraining';
  if (s.includes('yoga'))                                                   return 'Yoga';
  if (s.includes('hiit') || s.includes('crossfit'))                        return 'HIIT';
  return 'Workout';
}
